import { useEffect, useState, useCallback, useRef } from 'react';
import * as ss from 'simple-statistics';
import * as d3 from 'd3';
import { useQuery, gql } from '@apollo/client';
import { defaultScope as scope } from '../utility/getScope';

const GetScrollmapDataDocument = gql`
  query GetScrollmapData(
    $id: ID!
    $identifierType: String!
    $scope: ChartScopeInput!
    $breakdowns: [ChartBreakdownInput!]
  ) {
    chart(id: $id, identifierType: $identifierType, scope: $scope, breakdowns: $breakdowns) {
      header {
        breakdowns {
          breakdown
          filter
        }
      }
      table {
        columns
        rows
      }
    }
  }
`;

export const useScrollmap = (
  organization: string,
  project: string,
  round: string,
  variation: string,
  segment: string | null = null,
  dataLimit?: number,
) => {
  const [imageReady, setImageReady] = useState(false);
  const [invalidData, setInvalidData] = useState<string | null>(null);
  const [renderedSvg, setRenderedSvg] = useState<d3.Selection<SVGSVGElement, unknown, HTMLElement, any> | null>();
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (renderedSvg) {
      renderedSvg.selectAll('svg').remove();
      setRenderedSvg(null);
    }
  }, [organization, project, round, variation, segment]);

  useEffect(() => {
    if (!containerRef.current) {
      setImageReady(false);
    }
  }, [round, variation, segment]);

  const resizeObserver = new ResizeObserver((entries: Array<ResizeObserverEntry>) => {
    const height = entries[0].contentRect.height;
    if (height >= 200) {
      setImageReady(true);
      //if Image get ready after getScrollData but so far haven't detected this
      //!renderedSvg && render();
    }
  });

  useEffect(() => {
    containerRef.current && resizeObserver.observe(containerRef.current);
    return (): void => {
      //unmount
      resizeObserver.disconnect();
    };
  }, []);

  const render = useCallback(
    (pdf, invalid) => {
      if (imageReady && pdf) {
        const element = d3.select(`#${containerRef.current!.id}`);
        //const { width, height } = (element.node() as Element).getBoundingClientRect();
        const { width: innerWidth, height: innerHeight } = (
          element.select('img').node() as Element
        ).getBoundingClientRect();
        const lins = Array.from(Array(Math.round(innerHeight)), (_, index) => index * (1 / Math.round(innerHeight)));
        const density = lins.map((sp) => {
          return pdf(sp);
        });
        const data = {
          value: lins,
          density: density,
          pair: lins.map((v, i): [number, number] => {
            return [v, density[i]];
          }),
        };

        d3
          .scaleLinear() /* x = */
          .domain(d3.extent(data.value) as [number, number])
          .range([0, innerWidth]),
          d3
            .scaleLinear() /* y = */
            .domain([0, d3.max(data.density) as number])
            .range([0, innerHeight]);

        const color = d3.scaleSequentialSqrt([0, d3.max(data.density) as number], d3.interpolateTurbo);
        const svg = element
          .append('svg')
          .attr('width', '100%')
          .attr('height', '100%')
          .style('position', 'absolute')
          .style('top', '0')
          .style('left', '0');

        svg
          .selectAll('rect .bar')
          .data(data.value.map((_, i) => i))
          .enter()
          .append('rect')
          .classed('bar', true)
          .attr('y', (d) => d - 1)
          .attr('width', '100%')
          .attr('height', '1px')
          .attr('fill', (_, i) => color(!invalid ? data.density[i] : 0))
          .style('position', 'absolute')
          .style('top', '0')
          .style('left', '0')
          .style('opacity', '0.7');

        setRenderedSvg(svg);
        !invalid && setInvalidData(null);
      }
    },
    [imageReady, renderedSvg],
  );

  const { error, loading } = useQuery(GetScrollmapDataDocument, {
    fetchPolicy: 'cache-first',
    variables: {
      id: `planbcd_scrollmap_${organization}`,
      identifierType: 'resource_key',
      ...scope,
      breakdowns: [
        { breakdown: 'project_id', filter: [project] },
        { breakdown: 'round_id', filter: [round] },
        { breakdown: 'variation_id', filter: [variation] },
        { breakdown: 'segment', filter: segment ? [segment] : null },
      ],
      options: {
        sampling: {
          type: 'SIZE',
          number: 40000, // sample size used in Sitedock v1
        },
      },
      ...(dataLimit
        ? {
            options: {
              sampling: {
                type: 'RATE',
                number: dataLimit,
              },
            },
          }
        : {}),
    },
    context: {
      clientName: 'dataplatform',
    },
    onCompleted: (data) => {
      const dataIndex = data.chart.table.columns.indexOf('json_data');
      const scrollData = data.chart.table.rows.flatMap((row: any) => {
        const data = JSON.parse(row[dataIndex].split('"').join(''));
        const rowProcessed = data.flatMap(([position, count]: [number, number]) => {
          return Array(count).fill(position);
        });
        return rowProcessed;
      });
      // data ranges 0.005~1.000, max 200 data points
      //const lins = Array.from(Array(200), (_, index) => index * (1/200));

      const invalid = !scrollData.length;
      if (!invalid) setInvalidData('no data'); // override density color to dark drey

      //kde returns probability density function (PDF)
      //helful description of kde: https://www.youtube.com/watch?v=x5zLaWT5KPs
      const pdf = ss.kernelDensityEstimation(scrollData.length ? scrollData : [0.4, 0.5]);
      render(pdf, invalid);
    },
  });

  return { containerRef, error, loading, invalidData };
};
